import React, { useState, useEffect, useRef, useCallback } from "react";

const LANGUAGES = [
  { code: "en", label: "English", native: "English" },
  { code: "hi", label: "Hindi", native: "हिन्दी" },
  { code: "te", label: "Telugu", native: "తెలుగు" },
];

let gtScriptLoaded = false;

function loadGoogleTranslateScript() {
  return new Promise((resolve) => {
    if (gtScriptLoaded) { resolve(); return; }

    // Hidden container for the Google Translate widget
    let container = document.getElementById("gt_translate_element");
    if (!container) {
      container = document.createElement("div");
      container.id = "gt_translate_element";
      container.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
      document.body.appendChild(container);
    }

    window.__gtCallback = () => {
      gtScriptLoaded = true;
      try {
        const TE = window.google.translate.TranslateElement;
        new TE(
          { pageLanguage: "en", includedLanguages: "en,hi,te", autoDisplay: false },
          "gt_translate_element"
        );
      } catch (e) {
        console.warn("Google Translate init error:", e);
      }
      resolve();
    };

    const script = document.createElement("script");
    script.src =
      "https://translate.google.com/translate_a/element.js?cb=__gtCallback";
    script.async = true;
    script.onerror = () => {
      console.warn("Google Translate script failed to load");
      resolve(); // resolve anyway so UI doesn't hang
    };
    document.head.appendChild(script);
  });
}

function triggerTranslation(langCode) {
  // Wait briefly for the combo to appear, then trigger
  const attempt = (retries) => {
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      combo.value = langCode === "en" ? "en" : langCode;
      combo.dispatchEvent(new Event("change"));
      // For "en" we need to reset - trigger the restore bar
      if (langCode === "en") {
        // Try clicking the "Show original" link if present
        const restore = document.querySelector(".goog-te-banner-frame");
        if (restore) {
          try {
            const innerDoc =
              restore.contentDocument || restore.contentWindow.document;
            const btn = innerDoc.querySelector(".goog-close-link");
            if (btn) btn.click();
          } catch (e) { /* cross-origin, fall back to combo */ }
        }
        // Also set via cookie
        document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
      }
    } else if (retries > 0) {
      setTimeout(() => attempt(retries - 1), 300);
    }
  };
  attempt(10);
}

export default function LanguageSelector() {
  const [current, setCurrent] = useState("en");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Detect if already translated (page reload scenario)
  useEffect(() => {
    const cookie = document.cookie
      .split(";")
      .find((c) => c.trim().startsWith("googtrans="));
    if (cookie) {
      const val = cookie.split("=")[1];
      const match = val.match(/\/en\/(\w+)/);
      if (match && match[1] !== "en") {
        setCurrent(match[1]);
      }
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectLanguage = useCallback(async (langCode) => {
    if (langCode === current) { setOpen(false); return; }
    setLoading(true);
    setOpen(false);
    setCurrent(langCode);

    await loadGoogleTranslateScript();
    triggerTranslation(langCode);

    // Small delay then clear loading
    setTimeout(() => setLoading(false), 800);
  }, [current]);

  const currentLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-selector" ref={ref}>
      <button
        className="lang-selector-btn"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading}
      >
        <svg className="lang-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        <span className="lang-current">{currentLang.native}</span>
        <svg className={`lang-chevron${open ? " open" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div className="lang-dropdown" role="listbox">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              className={`lang-option${lang.code === current ? " active" : ""}`}
              onClick={() => selectLanguage(lang.code)}
              role="option"
              aria-selected={lang.code === current}
            >
              <span className="lang-option-native">{lang.native}</span>
              <span className="lang-option-label">{lang.label}</span>
              {lang.code === current && (
                <svg className="lang-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
